import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui";
import { TrendingUp, TrendingDown, Calculator, Loader2, Lightbulb, X, CheckCircle2, DollarSign, BarChart3, Shield, Lock, HelpCircle, Info } from "lucide-react";
import { companyApi } from "@/services/api";
import { cn } from "@/lib/utils";

interface HedgeSimulatorModalProps {
  open: boolean;
  onClose: () => void;
}

// Steps do loading animado
const LOADING_STEPS = [
  {
    id: "connect",
    message: "Conectando ao mercado de câmbio...",
    icon: <DollarSign className="w-4 h-4" />,
    duration: 800,
  },
  {
    id: "rate",
    message: "Buscando cotação atual do dólar...",
    icon: <BarChart3 className="w-4 h-4" />,
    duration: 1200,
  },
  {
    id: "calculate",
    message: "Calculando cenários de proteção...",
    icon: <Shield className="w-4 h-4" />,
    duration: 1000,
  },
];

function formatBrl(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });
}

function formatUsdcValue(value: number): string {
  return value.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }) + " USDC";
}

export function HedgeSimulatorModal({ open, onClose }: HedgeSimulatorModalProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(30000);
  const [days, setDays] = useState(60);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<string[]>([]);
  const [showContent, setShowContent] = useState(false);

  // Reset ao abrir
  useEffect(() => {
    if (open) {
      setValue(30000);
      setDays(60);
      setCurrentStep(0);
      setCompletedSteps([]);
      setShowContent(false);
    }
  }, [open]);

  // Buscar taxa real
  const { data: rateData, isLoading, isFetched } = useQuery({
    queryKey: ["exchange-rate"],
    queryFn: () => companyApi.conversion.getExchangeRate(),
    enabled: open,
    staleTime: 30 * 1000,
  });

  // Animação dos steps de loading
  useEffect(() => {
    if (!open || !isLoading) return;

    let stepIndex = 0;
    setCurrentStep(0);
    setCompletedSteps([]);

    const runStep = () => {
      if (stepIndex >= LOADING_STEPS.length) return;

      const step = LOADING_STEPS[stepIndex];

      const timeout = setTimeout(() => {
        setCompletedSteps((prev) => [...prev, step.id]);
        stepIndex++;
        setCurrentStep(stepIndex);

        if (stepIndex < LOADING_STEPS.length) {
          runStep();
        }
      }, step.duration);

      return timeout;
    };

    const timeout = runStep();

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [open, isLoading]);

  // Mostrar conteúdo quando dados carregarem
  useEffect(() => {
    if (isFetched && !isLoading) {
      const timeout = setTimeout(() => {
        setShowContent(true);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [isFetched, isLoading]);

  const currentRate = rateData?.rate || 5.50;
  const VARIATION = 0.10;

  // Cálculos de cenários
  const baseAmount = value * currentRate;
  const upRate = currentRate * (1 + VARIATION);
  const downRate = currentRate * (1 - VARIATION);
  const upAmount = value * upRate;
  const downAmount = value * downRate;

  // Cálculo da Taxa de Proteção estimada
  // Taxa = Taxa Fixa (0.30%) + Custo de Capital (6% a.a. proporcional ao prazo)
  const OPERATION_FEE_PERCENTAGE = 0.003; // 0.30%
  const CAPITAL_COST_PERCENTAGE = 0.06; // 6% a.a.
  const MIN_OPERATION_FEE_BRL = 50;

  const hedgeAmountBrl = value * currentRate;
  const durationMonths = days / 30;

  const operationFeeBrl = Math.max(hedgeAmountBrl * OPERATION_FEE_PERCENTAGE, MIN_OPERATION_FEE_BRL);
  const capitalCostBrl = hedgeAmountBrl * CAPITAL_COST_PERCENTAGE * (durationMonths / 12);
  const protectionFeeBrl = operationFeeBrl + capitalCostBrl;

  // Valor final com fee (valor travado)
  const lockedAmountBrl = baseAmount + protectionFeeBrl;

  const handleContratarProtecao = () => {
    onClose();
    navigate("/protecao-cambial/novo");
  };

  if (!open) return null;

  return (
    <TooltipProvider>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <div className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                <CardTitle>Simulador de Proteção Cambial</CardTitle>
              </div>
              <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
                Simule cenários e veja como manter seu custo previsível em reais usando USDC.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </CardHeader>

          <CardContent className="space-y-5">
            {(isLoading || !showContent) ? (
              <div className="py-6 space-y-6">
                {/* Header com animação */}
                <div className="text-center space-y-2">
                  <div className="relative mx-auto w-14 h-14">
                    <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                    <div className="relative w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                      <Calculator className="w-7 h-7 text-primary animate-pulse" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mt-4">Preparando Simulação</h3>
                  <p className="text-sm text-muted-foreground">Buscando dados do mercado...</p>
                </div>

                {/* Steps animados */}
                <div className="space-y-2 max-w-sm mx-auto">
                  {LOADING_STEPS.map((step, index) => {
                    const isCompleted = completedSteps.includes(step.id);
                    const isCurrent = index === currentStep && !isCompleted;
                    const isPending = index > currentStep;

                    return (
                      <div
                        key={step.id}
                        className={cn(
                          "flex items-center gap-3 p-2.5 rounded-lg transition-all duration-300",
                          isCompleted && "bg-green-50 dark:bg-green-950/30",
                          isCurrent && "bg-primary/5",
                          isPending && "opacity-40"
                        )}
                      >
                        <div
                          className={cn(
                            "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300",
                            isCompleted && "bg-green-500 text-white",
                            isCurrent && "bg-primary text-primary-foreground",
                            isPending && "bg-muted text-muted-foreground"
                          )}
                        >
                          {isCompleted ? (
                            <CheckCircle2 className="w-4 h-4" />
                          ) : isCurrent ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            step.icon
                          )}
                        </div>
                        <span
                          className={cn(
                            "text-sm transition-all duration-300",
                            isCompleted && "text-green-700 dark:text-green-300 font-medium",
                            isCurrent && "text-foreground font-medium",
                            isPending && "text-muted-foreground"
                          )}
                        >
                          {step.message}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {/* Sliders */}
                <div className="space-y-4 bg-muted/30 rounded-lg p-4">
                  {/* Valor a proteger */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Valor a proteger (USDC)</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>Quantidade em USDC (dólar digital) usada para estruturar sua proteção cambial.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        min={1000}
                        max={500000}
                        step={1000}
                        value={value}
                        onChange={(e) => setValue(Number(e.target.value))}
                      />
                      <span className="text-right font-semibold w-32 text-primary">{formatUsdcValue(value)}</span>
                    </div>
                  </div>

                  {/* Período de proteção */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-1.5">
                      <Label className="text-sm font-medium">Período de proteção</Label>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <HelpCircle className="w-3.5 h-3.5 text-muted-foreground cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>Prazo durante o qual o valor em reais ficará protegido contra variações do dólar.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                    <div className="flex items-center gap-4">
                      <input
                        type="range"
                        className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                        min={30}
                        max={180}
                        step={15}
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                      />
                      <span className="text-right font-semibold w-32 text-primary">{days} dias</span>
                    </div>
                  </div>
                </div>

                {/* Cards - Responsivos */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
                  {/* Card Dólar Cai */}
                  <Card className="order-2 md:order-1 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="font-medium text-green-700 dark:text-green-300">Se o dólar cair 10%</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-neutral-400">Sem proteção:</span>
                          <span className="font-semibold text-neutral-900 dark:text-white">{formatBrl(downAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-neutral-400">Com proteção:</span>
                          <span className="font-semibold text-neutral-900 dark:text-white">{formatBrl(lockedAmountBrl)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 pt-3 border-t border-green-200 dark:border-green-800">
                        Você pagaria menos sem proteção, mas seu custo ficaria exposto à variação do câmbio.
                      </p>
                    </CardContent>
                  </Card>

                  {/* Card Central - Valor Fixo Garantido */}
                  <Card className="order-1 md:order-2 bg-primary/5 dark:bg-primary/10 border-primary/20 dark:border-primary/30">
                    <CardContent className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Lock className="w-4 h-4 text-primary" />
                        <span className="font-medium text-primary">Valor fixo garantido em reais</span>
                      </div>
                      <p className="text-3xl font-bold text-primary my-3">{formatBrl(lockedAmountBrl)}</p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-3">
                        Esse será o valor do seu pagamento, independentemente da variação do dólar.
                      </p>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>Baseado em uma taxa de referência de R$ {currentRate.toFixed(4)}</p>
                        <p className="text-xs">Fee de proteção: {formatBrl(protectionFeeBrl)} ({days} dias)</p>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Card Dólar Sobe */}
                  <Card className="order-3 bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                        <span className="font-medium text-orange-700 dark:text-orange-300">Se o dólar subir 10%</span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-neutral-400">Sem proteção:</span>
                          <span className="font-semibold text-neutral-900 dark:text-white">{formatBrl(upAmount)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-neutral-600 dark:text-neutral-400">Com proteção:</span>
                          <span className="font-semibold text-neutral-900 dark:text-white">{formatBrl(lockedAmountBrl)}</span>
                        </div>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 pt-3 border-t border-orange-200 dark:border-orange-800">
                        Você evita pagar mais e mantém previsibilidade no seu caixa.
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Bloco Resumo */}
                <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2">
                      <p className="font-semibold text-blue-800 dark:text-blue-200">Em resumo</p>
                      <div className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                        <p><strong>Com proteção:</strong> custo fixo de {formatBrl(lockedAmountBrl)}</p>
                        <p><strong>Sem proteção:</strong> custo pode variar de {formatBrl(downAmount)} a {formatBrl(upAmount)}</p>
                        <p className="text-xs text-blue-500">
                          Fee de proteção incluso: {formatBrl(protectionFeeBrl)} (custo total para manter o valor protegido por {days} dias)
                        </p>
                      </div>
                      <p className="text-sm text-blue-600 dark:text-blue-400 pt-1">
                        A proteção cambial com USDC elimina a incerteza do dólar no seu planejamento financeiro.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Mensagem de governança */}
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <p>
                    Esta simulação é voltada para proteção de caixa, utilizando USDC como base operacional.
                    Não se trata de uma operação para ganho com variação cambial e não gera alavancagem.
                  </p>
                </div>
              </>
            )}

            {/* Footer com botões */}
            {showContent && (
              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2">
                <Button variant="outline" className="w-full sm:w-auto" onClick={onClose}>
                  Fechar
                </Button>
                <Button className="w-full sm:w-auto" onClick={handleContratarProtecao}>
                  <Lock className="w-4 h-4 mr-2" />
                  Contratar proteção
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}
